import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useCursorContext } from '../hooks/useCursorContext';
import { featuredProjects, NikoProject } from '../data/portfolioData';
import { allProjects } from '../data/allProjects';
import { Marquee } from './Marquee';

gsap.registerPlugin(ScrollTrigger);

export function ProjectsGallery() {
  const containerRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const mobileSliderRef = useRef<HTMLDivElement>(null);
  const { setCursorState, setCursorText } = useCursorContext();
  const [activeMobileSlide, setActiveMobileSlide] = useState(0);
  const mobileProjects = featuredProjects.slice(0, 6);
  const mobileSlideCount = mobileProjects.length + 1;

  useEffect(() => {
    if (window.matchMedia('(max-width: 767px)').matches) {
      return;
    }

    const ctx = gsap.context(() => {
      const gallery = galleryRef.current;
      if (!gallery) return;

      // Calculate scroll distance
      const scrollWidth = gallery.scrollWidth - window.innerWidth;

      // Horizontal scroll animation
      gsap.to(gallery, {
        x: -scrollWidth,
        ease: 'none',
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          end: () => `+=${scrollWidth}`,
          pin: true,
          scrub: 1,
          invalidateOnRefresh: true,
        },
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const handleProjectHover = (hovering: boolean) => {
    if (hovering) {
      setCursorState('text');
      setCursorText('View');
    } else {
      setCursorState('default');
      setCursorText('');
    }
  };

  const scrollMobileSlider = (direction: 'prev' | 'next') => {
    const slider = mobileSliderRef.current;
    if (!slider) return;

    const slideWidth = getMobileSlideWidth(slider);
    slider.scrollBy({
      left: direction === 'next' ? slideWidth : -slideWidth,
      behavior: 'smooth',
    });
  };

  const handleMobileSliderScroll = () => {
    const slider = mobileSliderRef.current;
    if (!slider) return;

    const nextIndex = Math.round(slider.scrollLeft / getMobileSlideWidth(slider));
    setActiveMobileSlide(Math.min(Math.max(nextIndex, 0), mobileSlideCount - 1));
  };

  const getMobileSlideWidth = (slider: HTMLDivElement): number => {
    const firstSlide = slider.firstElementChild;
    if (!(firstSlide instanceof HTMLElement)) {
      return slider.clientWidth;
    }

    return firstSlide.getBoundingClientRect().width + 16;
  };

  const getProjectImage = (project: NikoProject) => {
    const colors: Record<string, string> = {
      'Software & KI': '8b5cf6',
      Automotive: '1C69D4',
      Energie: '10b981',
      'IT-Security': 'ef4444',
      Chemie: '84cc16',
      Pharma: 'ec4899',
      'Neon Product Lab': '10b981',
    };
    const color = project.industry ? colors[project.industry] || '10b981' : '10b981';
    return `https://placehold.co/800x600/${color}/0a0a0a?text=${encodeURIComponent(project.company)}`;
  };

  return (
    <section id="work" ref={containerRef} className="relative overflow-hidden">
      {/* Section header */}
      <div className="py-16 px-8 md:px-12">
        <span className="text-xs text-white/30 tracking-[0.3em] uppercase">
          Ausgewählte Projekte
        </span>
      </div>

      {/* Marquee */}
      <Marquee className="py-8 border-y border-white/5">
        {[
          'Neon Product Lab',
          '—',
          'NeoWispr • NeoVoice • Neon Core',
          '—',
          '12+ Jahre Erfahrung',
          '—',
          'Mercedes-Benz • E.ON • P&G',
          '—',
        ].map((text, i) => (
          <span
            key={i}
            className="text-lg md:text-2xl font-light text-white/20 uppercase tracking-wider"
          >
            {text}
          </span>
        ))}
      </Marquee>

      {/* Mobile touch carousel */}
      <div className="md:hidden">
        <div className="flex items-center justify-between px-6 pt-8">
          <span className="text-xs uppercase tracking-[0.26em] text-white/30">
            Swipe Projekte
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollMobileSlider('prev')}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/60 active:scale-95"
              aria-label="Vorheriges Projekt"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollMobileSlider('next')}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-300 active:scale-95"
              aria-label="Nächstes Projekt"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          ref={mobileSliderRef}
          onScroll={handleMobileSliderScroll}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 py-8 scrollbar-hide"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {mobileProjects.map((project, index) => (
            <Link
              key={project.id}
              to={`/project/${project.id}`}
              className="block w-[82vw] max-w-[340px] flex-none snap-center rounded-3xl border border-white/10 bg-white/[0.03] p-3"
              onMouseEnter={() => handleProjectHover(true)}
              onMouseLeave={() => handleProjectHover(false)}
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-white/5">
                <img
                  src={getProjectImage(project)}
                  alt={project.title}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <span className="absolute left-4 top-4 text-xs tracking-wider text-white/50">
                  {String(index + 1).padStart(2, '0')}
                </span>
                {project.current && (
                  <span className="absolute right-4 top-4 rounded bg-emerald-500/90 px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-black">
                    Aktuell
                  </span>
                )}
              </div>

              <div className="min-h-[180px] space-y-4 p-3 pt-5">
                <div>
                  <h3 className="text-2xl font-light leading-tight text-white">
                    {project.title}
                  </h3>
                  <p className="mt-2 text-sm text-white/40">{project.company}</p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-emerald-400/20 px-3 py-1 text-xs text-emerald-300/80">
                    {project.industry}
                  </span>
                  <span className="text-xs text-white/30">{project.period}</span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {project.tech.slice(0, 3).map((tech) => (
                    <span
                      key={tech}
                      className="rounded bg-white/5 px-2 py-1 text-[11px] uppercase tracking-wider text-white/35"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          ))}

          <Link
            to="/work"
            className="flex w-[82vw] max-w-[340px] flex-none snap-center flex-col items-center justify-center rounded-3xl border border-emerald-400/25 bg-emerald-400/[0.06] p-8 text-center"
            onMouseEnter={() => handleProjectHover(true)}
            onMouseLeave={() => handleProjectHover(false)}
          >
            <div className="flex h-20 w-20 items-center justify-center rounded-full border border-emerald-400/40">
              <ArrowRight className="h-7 w-7 text-emerald-300" />
            </div>
            <span className="mt-6 text-xl text-white">Alle Arbeiten</span>
            <p className="mt-2 text-sm text-white/40">{allProjects.length} Projekte ansehen</p>
          </Link>
        </div>

        <div className="flex justify-center gap-2 pb-6">
          {Array.from({ length: mobileSlideCount }).map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => {
                const slider = mobileSliderRef.current;
                if (!slider) return;

                mobileSliderRef.current?.scrollTo({
                  left: index * getMobileSlideWidth(slider),
                  behavior: 'smooth',
                });
              }}
              className={`h-1.5 rounded-full transition-all ${
                activeMobileSlide === index ? 'w-8 bg-emerald-400' : 'w-2 bg-white/20'
              }`}
              aria-label={`Zu Slide ${index + 1}`}
              aria-current={activeMobileSlide === index ? 'true' : undefined}
            />
          ))}
        </div>
      </div>

      {/* Horizontal scroll gallery */}
      <div
        ref={galleryRef}
        className="hidden px-6 py-12 sm:px-8 md:flex md:w-max md:flex-row md:gap-8 md:py-16 md:pl-12 md:pr-0"
      >
        {featuredProjects.map((project, index) => (
          <Link
            key={project.id}
            to={`/project/${project.id}`}
            className="relative group block w-full cursor-pointer md:w-[50vw] md:flex-shrink-0 lg:w-[40vw]"
            onMouseEnter={() => handleProjectHover(true)}
            onMouseLeave={() => handleProjectHover(false)}
          >
            {/* Image container */}
            <div className="relative aspect-[4/3] overflow-hidden bg-white/5 rounded-lg">
              <img
                src={getProjectImage(project)}
                alt={project.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />

              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {/* Project number */}
              <span className="absolute top-4 left-4 text-xs text-white/40 tracking-wider">
                {String(index + 1).padStart(2, '0')}
              </span>

              {/* Current badge */}
              {project.current && (
                <span className="absolute top-4 right-4 px-2 py-1 text-[10px] bg-emerald-500/90 text-black rounded tracking-wider uppercase font-medium">
                  Aktuell
                </span>
              )}

              {/* View button on hover */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-sm text-white tracking-wider">
                  Projekt ansehen
                </span>
              </div>
            </div>

            {/* Project info */}
            <div className="mt-6 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl md:text-2xl font-light text-white group-hover:text-emerald-500 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-sm text-white/40 mt-1">{project.company}</p>
                </div>
                <span className="text-xs text-white/30">{project.period}</span>
              </div>

              {/* Industry & Tech */}
              <div className="flex items-center gap-3 pt-2">
                <span className="text-xs text-emerald-500/80">{project.industry}</span>
                <span className="text-white/20">•</span>
                <div className="flex flex-wrap gap-2">
                  {project.tech.slice(0, 3).map((tech) => (
                    <span
                      key={tech}
                      className="text-xs text-white/30 uppercase tracking-wider"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Link>
        ))}

        {/* View All Projects CTA */}
        <div className="flex w-full items-center justify-center md:w-[30vw] md:flex-shrink-0">
          <Link
            to="/work"
            className="group flex w-full flex-col items-center gap-4 rounded-3xl border border-white/10 p-8 md:w-auto md:border-0"
            onMouseEnter={() => handleProjectHover(true)}
            onMouseLeave={() => handleProjectHover(false)}
          >
            <div className="w-24 h-24 flex items-center justify-center border border-white/20 rounded-full group-hover:border-emerald-500 group-hover:bg-emerald-500/10 transition-colors">
              <ArrowRight className="w-8 h-8 text-white/60 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" />
            </div>
            <div className="text-center">
              <span className="text-xl text-white group-hover:text-emerald-500 transition-colors">
                Alle Arbeiten
              </span>
              <p className="text-sm text-white/40 mt-1">
                {allProjects.length} Projekte ansehen
              </p>
            </div>
          </Link>
        </div>

        {/* End spacer */}
        <div className="hidden w-[10vw] flex-shrink-0 md:block" />
      </div>
    </section>
  );
}
